import { useCallback, useMemo } from 'react';
import { Table, Empty, Spin } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useTheme } from 'styled-components';
import Button from 'components/Base/Button/Button';
import { Select } from 'components/Base/Select';
import { Title, Text, SecondaryText } from 'components/Base/Typography';
import {
  useGetInKindYearsQuery,
  useLazyExportInKindTransactionsQuery,
} from 'redux/apiSlice/inKind.slice';
import { TInKindCategoryDB } from 'data/types/inKind.types';
import { TYearSummaryProps } from './InKindTab.types';
import { QUARTER_DATE_RANGES, formatDollarValue } from './InKindTab.data';
import {
  SContentWrapper,
  SHeaderRow,
  SHeaderLeft,
  SYtdBanner,
  SQuarterGrid,
  SQuarterTile,
  SEmptyWrapper,
} from './InKindTab.styles';

const CURRENT_YEAR = new Date().getFullYear();

const YearSummary = ({
  elcId,
  selectedYear,
  data,
  isLoading,
  onYearChange,
  onQuarterClick,
}: TYearSummaryProps) => {
  const theme = useTheme();
  const { data: yearsData } = useGetInKindYearsQuery({ elcId });
  const [triggerExport, { isFetching: isExporting }] =
    useLazyExportInKindTransactionsQuery();

  const handleExportYear = () => {
    triggerExport({ elcId, year: selectedYear });
  };

  const quarters = useMemo(() => {
    if (!data) return [];
    return [
      { quarter: 1, ...data.q1 },
      { quarter: 2, ...data.q2 },
      { quarter: 3, ...data.q3 },
      { quarter: 4, ...data.q4 },
    ];
  }, [data]);

  const categoryColumns = [
    {
      title: 'Category',
      dataIndex: 'categoryName',
      key: 'categoryName',
    },
    {
      title: 'Type',
      key: 'type',
      width: 120,
      render: (_: unknown, record: TInKindCategoryDB) => {
        const type = record.transactions?.[0]?.type;
        return type
          ? type.charAt(0).toUpperCase() + type.slice(1).toLowerCase()
          : '-';
      },
    },
    {
      title: 'Items',
      dataIndex: 'totalQuantity',
      key: 'totalQuantity',
      width: 120,
      align: 'right' as const,
      render: (_: unknown, record: TInKindCategoryDB) => {
        const type = record.transactions?.[0]?.type;
        if (type === 'TIME' || type === 'Time') {
          const str = String(record.totalQuantity);
          const match = str.match(/^(\d+):(\d+)$/);
          if (match) {
            const h = parseInt(match[1], 10);
            const m = parseInt(match[2], 10);
            return `${h}h ${m}m`;
          }
        }
        return typeof record.totalQuantity === 'number'
          ? Math.round(record.totalQuantity)
          : record.totalQuantity;
      },
    },
    {
      title: 'YTD Total',
      dataIndex: 'totalDollarValue',
      key: 'totalDollarValue',
      width: 160,
      align: 'right' as const,
      render: (val: number) => formatDollarValue(val),
    },
  ];

  const isEmpty = !data || data.totalTransactions === 0;

  const renderCategorySummaryFooter = useCallback(
    () => (
      <Table.Summary.Row>
        <Table.Summary.Cell index={0}>
          <Text fontWeight={600}>Total</Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={1} />
        <Table.Summary.Cell index={2} align="right">
          <Text fontWeight={600}>{data?.yearToDate.totalItems ?? 0}</Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={3} align="right">
          <Text fontWeight={600} color={theme.colorPrimaryDefault}>
            {formatDollarValue(data?.yearToDate.totalDollarValue ?? 0)}
          </Text>
        </Table.Summary.Cell>
      </Table.Summary.Row>
    ),
    [data?.yearToDate.totalItems, data?.yearToDate.totalDollarValue],
  );

  const yearOptions = useMemo(() => {
    const apiYears = yearsData && yearsData.length > 0 ? yearsData : [];
    // Always include the current year so users can view/add data for it
    const years = apiYears.includes(CURRENT_YEAR)
      ? apiYears
      : [CURRENT_YEAR, ...apiYears];
    return years
      .slice()
      .sort((a, b) => b - a)
      .map(y => ({ label: String(y), value: y }));
  }, [yearsData]);

  if (isLoading) {
    return (
      <SContentWrapper>
        <Spin style={{ display: 'block', margin: '80px auto' }} />
      </SContentWrapper>
    );
  }

  return (
    <SContentWrapper>
      <SHeaderRow>
        <SHeaderLeft>
          <Title level={4} className="mb-0">
            In-Kind Donations
          </Title>
          <Select
            value={selectedYear}
            onChange={onYearChange}
            options={yearOptions}
            style={{ width: 110 }}
          />
        </SHeaderLeft>
        <Button
          variant="default"
          icon={<DownloadOutlined />}
          onClick={handleExportYear}
          disabled={isEmpty || isExporting}
          loading={isExporting}
          aria-label="Export year to CSV"
        >
          Export Year CSV
        </Button>
      </SHeaderRow>

      {isEmpty ? (
        <SEmptyWrapper>
          <Empty
            description={`No in-kind donations recorded for ${selectedYear}.`}
          />
        </SEmptyWrapper>
      ) : (
        <>
          {/* YTD Banner */}
          <SYtdBanner>
            <div>
              <Text color="#595959">Year-to-Date Total</Text>
              <Title
                level={2}
                color={theme.colorPrimaryDefault}
                className="mb-0"
              >
                {formatDollarValue(data.yearToDate.totalDollarValue)}
              </Title>
            </div>
            <SecondaryText>
              {data.yearToDate.totalItems} donation
              {data.yearToDate.totalItems !== 1 ? 's' : ''} across{' '}
              {data.yearToDate.totalCategories} categor
              {data.yearToDate.totalCategories !== 1 ? 'ies' : 'y'}
            </SecondaryText>
          </SYtdBanner>

          {/* Quarter Tiles */}
          <Title level={4} className="mb-4">
            Quarterly Breakdown
          </Title>
          <SQuarterGrid>
            {quarters.map(q => {
              const hasData = q.totalTransactions > 0;
              return (
                <SQuarterTile
                  type="button"
                  key={q.quarter}
                  $hasData={hasData}
                  onClick={() => {
                    if (hasData) onQuarterClick(q.quarter);
                  }}
                  disabled={!hasData}
                  aria-label={`Q${q.quarter} ${
                    QUARTER_DATE_RANGES[q.quarter]
                  } - ${
                    hasData ? formatDollarValue(q.totalDollarValue) : 'No data'
                  }`}
                >
                  <SecondaryText>
                    Q{q.quarter} ({QUARTER_DATE_RANGES[q.quarter]})
                  </SecondaryText>
                  <Title
                    level={4}
                    color={hasData ? '#262626' : '#bfbfbf'}
                    className="mb-0"
                  >
                    {hasData ? formatDollarValue(q.totalDollarValue) : '$0'}
                  </Title>
                  {hasData && (
                    <SecondaryText>
                      {q.totalTransactions} item
                      {q.totalTransactions !== 1 ? 's' : ''} &middot; Click for
                      details
                    </SecondaryText>
                  )}
                </SQuarterTile>
              );
            })}
          </SQuarterGrid>

          {/* Category Summary Table */}
          <Title level={4} className="mb-4">
            Category Summary
          </Title>
          <Table
            dataSource={data.categories}
            columns={categoryColumns}
            rowKey="categoryId"
            pagination={false}
            size="middle"
            summary={renderCategorySummaryFooter}
          />
        </>
      )}
    </SContentWrapper>
  );
};

export default YearSummary;
